import { Theme } from '@radix-ui/themes'
import { SimpleFormField } from 'components/form/formTypes'
import { CoordinateInputTablePointAndClickApi } from 'components/form/inputs/coordinatesInput/CoordinatesInputTable'
import { Coordinate } from 'components/form/inputs/coordinatesInput/helpers/coordinateTranslations'
import React from 'react'
import {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import { FormField, GeckoMeasurementUnit } from 'types'

import { RenderFormField } from './RenderFormField'

const StyledFieldContainer = styled.div`
  display: contents;
  min-height: 0px;
`

export interface BaseFormFieldProps {
  formItemKey: string
  formField: FormField
  fieldValue?: string | number | string[] | undefined
  fieldUnits?: string | undefined
  units?: GeckoMeasurementUnit | null
  fieldMode?: 'edit' | 'read'
  fieldColor?: 'default' | 'warning'
  formControl: Control<SimpleFormField>
  formErrors: FieldErrors<SimpleFormField>
  formTrigger: UseFormTrigger<SimpleFormField>
  formSetValue: UseFormSetValue<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is the actual type
    [x: string]: any
  }>
  showLabel?: boolean
  ariaLabelledBy?: string
  pointAndClickApi: CoordinateInputTablePointAndClickApi | undefined
  highlightCoordinate: (coordinate: Coordinate | null) => void
  displayCoordinateUpdates: (newCoords: ReadonlyArray<Coordinate>) => void
}

export const BaseFormField = (props: BaseFormFieldProps) => {
  const {
    formField,
    formItemKey,
    formControl,
    formErrors,
    formTrigger,
    formSetValue,
    fieldValue,
    fieldUnits,
    units,
    fieldMode,
    fieldColor,
    showLabel = true,
    ariaLabelledBy,
    pointAndClickApi,
    highlightCoordinate,
    displayCoordinateUpdates,
  } = props
  const { theme } = useGeckoTheme()

  return (
    <Theme
      asChild
      key={formItemKey}
      hasBackground={false}
      appearance='dark'
      accentColor='gray'
      grayColor='slate'
      radius='none'>
      <StyledFieldContainer>
        <RenderFormField
          key={formItemKey}
          formItemLabel={showLabel ? formField.label : undefined}
          ariaLabelledBy={ariaLabelledBy}
          fieldValue={fieldValue ? fieldValue : formField.value}
          fieldUnits={fieldUnits}
          formItemKey={formItemKey}
          formItem={formField}
          formControl={formControl}
          formErrors={formErrors}
          formTrigger={formTrigger}
          formSetValue={formSetValue}
          units={units ? units : null}
          highlightCoordinate={highlightCoordinate}
          fieldMode={fieldMode ? fieldMode : 'edit'}
          borderColor={
            fieldColor === 'warning'
              ? `color-mix(in srgb, ${theme.forecast.warning} 60%, ${theme.forecast.secondaryText} 40%)`
              : `${theme.forecast.border2}`
          }
          labelColor={
            fieldColor === 'warning'
              ? `color-mix(in srgb, ${theme.forecast.warning} 70%, ${theme.forecast.activeText} 30%)`
              : undefined
          }
          pointAndClickApi={pointAndClickApi}
          displayCoordinateUpdates={displayCoordinateUpdates}
        />
      </StyledFieldContainer>
    </Theme>
  )
}
